// Display mirror of backend/src/lib/supplierContracts.ts — the labels the
// contract register shows and the same date arithmetic, so a card reads the
// same way the API and the register document do. The server is the authority:
// nothing here is ever sent as a verdict, only used to draw.

export const CONTRACT_KINDS = [
  "msa", "sow", "dpa", "nda", "sla", "licence", "support", "framework", "order", "other",
] as const;
export type ContractKind = (typeof CONTRACT_KINDS)[number];

export const CONTRACT_KIND_LABELS: Record<ContractKind, string> = {
  msa:       "Master services agreement",
  sow:       "Statement of work",
  dpa:       "Data processing agreement",
  nda:       "Non-disclosure agreement",
  sla:       "Service level agreement",
  licence:   "Licence agreement",
  support:   "Support / maintenance agreement",
  framework: "Framework agreement",
  order:     "Purchase order / order form",
  other:     "Other agreement",
};

export const CONTRACT_STATUSES = ["draft", "active", "expired", "terminated"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export type TermScope = "always" | "processor" | "important" | "processor_or_important";

export interface ContractTerm {
  key: string;
  label: string;
  detail: string;
  basis: string;
  scope: TermScope;
}

export const CONTRACT_TERMS: readonly ContractTerm[] = [
  { key: "securityRequirements", scope: "always", label: "Information security requirements", detail: "The security the supplier must maintain is written into the agreement, not left to its own policy of the day.", basis: "ISO 27001 A.5.20; NIST CSF GV.SC-05" },
  { key: "confidentiality", scope: "always", label: "Confidentiality", detail: "Confidentiality survives the agreement and binds the supplier's own staff and subcontractors.", basis: "ISO 27001 A.6.6" },
  { key: "incidentNotification", scope: "always", label: "Incident notification duty", detail: "The supplier must tell you about an incident affecting you, within a stated window — early enough to meet your own regulatory clocks.", basis: "ISO 27001 A.5.20 / A.5.26; NIS2 Art. 21(2)(d); DORA Art. 30(3)(a)" },
  { key: "subcontractorControl", scope: "always", label: "Subcontractor and supply-chain control", detail: "The supplier may not pass the work down the chain without notice or consent, and its subcontractors are held to the same terms.", basis: "ISO 27001 A.5.21; GDPR Art. 28(2) and (4)" },
  { key: "auditRights", scope: "processor_or_important", label: "Audit and assurance rights", detail: "You may inspect, audit or receive independent assurance over the service — not merely ask for a certificate — and a processor makes available the information needed to show it complies.", basis: "GDPR Art. 28(3)(h); DORA Art. 30(3)(e); ISO 27001 A.5.22" },
  { key: "serviceLevels", scope: "important", label: "Service levels and availability", detail: "Availability, performance and support-response targets are stated, with what happens when they are missed.", basis: "ISO 27001 A.5.22 / A.8.14; DORA Art. 30(3)(b)" },
  { key: "businessContinuity", scope: "important", label: "Continuity and recovery commitments", detail: "The supplier's continuity and recovery obligations are stated and consistent with the RTO / RPO of what it delivers.", basis: "ISO 27001 A.5.30; DORA Art. 30(3)(c); NCSC CAF B5" },
  { key: "exitPlan", scope: "important", label: "Exit and transition assistance", detail: "On termination the supplier must hand over, keep the service running through transition and cooperate with the successor.", basis: "DORA Art. 28(8) and Art. 30(3)(f); ISO 27001 A.5.22" },
  { key: "terminationRights", scope: "always", label: "Termination rights", detail: "You may terminate for breach, for a material change in the supplier's risk, and where a regulator requires it.", basis: "DORA Art. 28(7); ISO 27001 A.5.22" },
  { key: "processingDetails", scope: "processor", label: "Subject matter and details of the processing", detail: "The subject matter and duration, the nature and purpose, the type of personal data, the categories of data subjects, and the controller's obligations and rights are set out.", basis: "GDPR / UK GDPR Art. 28(3) (opening words); DPJL 2018 Art. 19" },
  { key: "documentedInstructions", scope: "processor", label: "Processing only on documented instructions", detail: "The processor acts only on the controller's documented instructions, including on transfers, unless the law requires otherwise — in which case it tells the controller first.", basis: "GDPR Art. 28(3)(a)" },
  { key: "personnelConfidentiality", scope: "processor", label: "Confidentiality of the people processing the data", detail: "Everyone authorised to process the data has committed to confidentiality or is under a statutory duty of it.", basis: "GDPR Art. 28(3)(b)" },
  { key: "subProcessorConsent", scope: "processor", label: "Sub-processor authorisation", detail: "Sub-processors are named or generally authorised with notice of change and a right to object, and are bound by the same data protection obligations.", basis: "GDPR Art. 28(2), (3)(d) and (4)" },
  { key: "assistanceWithRights", scope: "processor", label: "Assistance with data subject requests", detail: "The processor helps the controller answer requests from data subjects, by appropriate technical and organisational measures.", basis: "GDPR Art. 28(3)(e)" },
  { key: "assistanceWithObligations", scope: "processor", label: "Assistance with security, breaches, DPIAs and consultation", detail: "The processor helps the controller with security of processing, breach notification, DPIAs and prior consultation, taking account of what it knows.", basis: "GDPR Art. 28(3)(f) (Art. 32 to 36)" },
  { key: "unlawfulInstructionNotice", scope: "processor", label: "Tells the controller of an unlawful instruction", detail: "The processor immediately tells the controller if, in its opinion, an instruction infringes data protection law.", basis: "GDPR Art. 28(3), final subparagraph" },
  { key: "returnOrDeletion", scope: "processor", label: "Return or deletion at the end", detail: "At the end of the service the data is returned or deleted at your choice, and copies removed unless the law requires otherwise.", basis: "GDPR Art. 28(3)(g)" },
  { key: "transferSafeguards", scope: "processor", label: "International transfer safeguards", detail: "Where data leaves the jurisdiction, the transfer mechanism is named in the agreement and matches the RoPA.", basis: "GDPR Chapter V; DPJL 2018 Part 7" },
  { key: "liabilityAndInsurance", scope: "important", label: "Liability and insurance", detail: "Liability for a security or data failure is not capped below what such a failure would cost, and the supplier carries cover.", basis: "ISO 27001 A.5.20; board risk appetite" },
];

export interface ContractRelationship {
  isDataProcessor: boolean;
  riskTier: string;
  deliversImportantService?: boolean;
  /** A RoPA activity names the supplier as its processor, flagged or not (backend ContractRelationship). */
  ropaProcessor?: boolean;
}

/** Answers stored under a key since split (backend LEGACY_TERM_KEYS), read as the items the old wording described. */
export const LEGACY_TERM_KEYS: Readonly<Record<string, readonly string[]>> = {
  dataProtection: ["processingDetails", "documentedInstructions"],
  assistanceWithRights: ["assistanceWithObligations"],
};

export function withLegacyTerms(answers: Record<string, boolean> | null | undefined): Record<string, boolean> {
  const out: Record<string, boolean> = { ...(answers ?? {}) };
  for (const [legacy, items] of Object.entries(LEGACY_TERM_KEYS)) {
    const v = answers?.[legacy];
    if (typeof v !== "boolean") continue;
    for (const item of items) if (!(item in (answers ?? {}))) out[item] = v;
  }
  delete out.dataProtection;
  return out;
}

export function termsRequiredFor(rel: ContractRelationship): ContractTerm[] {
  const important = rel.riskTier === "critical" || rel.riskTier === "high" || !!rel.deliversImportantService;
  const processor = rel.isDataProcessor || !!rel.ropaProcessor;
  return CONTRACT_TERMS.filter((t) =>
    t.scope === "always" || (t.scope === "processor" && processor) || (t.scope === "important" && important)
    || (t.scope === "processor_or_important" && (processor || important)));
}

export type ContractStanding = "draft" | "active" | "expiring" | "notice_due" | "expired" | "terminated" | "open_ended";

export const CONTRACT_STANDING_LABELS: Record<ContractStanding, string> = {
  draft:      "Draft",
  active:     "In force",
  expiring:   "Expiring",
  notice_due: "Notice decision due",
  expired:    "Expired",
  terminated: "Terminated",
  open_ended: "In force, no end date",
};

export const CONTRACT_EXPIRING_DAYS = 90;

const DAY = 86_400_000;
const daysBetween = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / DAY);

export interface ContractTiming {
  standing: ContractStanding;
  daysToEnd: number | null;
  noticeDeadline: Date | null;
  daysToNotice: number | null;
  renewalLocked: boolean;
}

export function contractTiming(
  c: { status: string; endDate: string | null; autoRenew: boolean; noticePeriodDays: number | null },
  now: Date = new Date(),
): ContractTiming {
  const end = c.endDate ? new Date(c.endDate) : null;
  const daysToEnd = end ? daysBetween(end, now) : null;
  const noticeDeadline = end && c.noticePeriodDays != null ? new Date(end.getTime() - c.noticePeriodDays * DAY) : null;
  const daysToNotice = noticeDeadline ? daysBetween(noticeDeadline, now) : null;
  const renewalLocked = !!(c.autoRenew && daysToNotice != null && daysToNotice < 0 && (daysToEnd ?? 0) >= 0);

  let standing: ContractStanding;
  if (c.status === "terminated") standing = "terminated";
  else if (c.status === "draft") standing = "draft";
  else if (c.status === "expired" || (daysToEnd != null && daysToEnd < 0)) standing = "expired";
  else if (daysToNotice != null && daysToNotice >= 0 && daysToNotice <= CONTRACT_EXPIRING_DAYS) standing = "notice_due";
  else if (daysToEnd != null && daysToEnd <= CONTRACT_EXPIRING_DAYS) standing = "expiring";
  else if (daysToEnd == null) standing = "open_ended";
  else standing = "active";

  return { standing, daysToEnd, noticeDeadline, daysToNotice, renewalLocked };
}

/** A contract still governs the relationship unless it is a draft, expired or terminated. */
export const contractInForce = (standing: ContractStanding) =>
  standing !== "draft" && standing !== "expired" && standing !== "terminated";

export interface TermCoverage {
  present: ContractTerm[];
  missing: ContractTerm[];
  unanswered: ContractTerm[];
}

export function termCoverage(answers: Record<string, boolean> | null | undefined, rel: ContractRelationship): TermCoverage {
  const a = withLegacyTerms(answers);
  const out: TermCoverage = { present: [], missing: [], unanswered: [] };
  for (const t of termsRequiredFor(rel)) {
    if (a[t.key] === true) out.present.push(t);
    else if (a[t.key] === false) out.missing.push(t);
    else out.unanswered.push(t);
  }
  return out;
}

/**
 * What a supplier's agreements cover between them: a term counts when any agreement *in force*
 * carries it (backend summariseSupplierContracts). An expired, terminated or draft agreement
 * covers nothing, and with none in force `inForce` is 0 and there is no agreement to have a gap
 * in: the gap is the absence itself, never "every term covered".
 */
export function combinedTermCoverage(
  contracts: ReadonlyArray<{ status: string; endDate: string | null; autoRenew: boolean; noticePeriodDays: number | null; terms: Record<string, boolean> | null }>,
  rel: ContractRelationship,
  now: Date = new Date(),
): { inForce: number; required: ContractTerm[]; gaps: ContractTerm[]; unanswered: ContractTerm[] } {
  const live = contracts.filter((c) => contractInForce(contractTiming(c, now).standing));
  const answered = new Map<string, boolean>();
  for (const c of live) {
    for (const [k, v] of Object.entries(withLegacyTerms(c.terms))) answered.set(k, (answered.get(k) ?? false) || v);
  }
  const required = termsRequiredFor(rel);
  const gaps = live.length === 0 ? [] : required.filter((t) => answered.get(t.key) !== true);
  return { inForce: live.length, required, gaps, unanswered: gaps.filter((t) => answered.get(t.key) === undefined) };
}
